import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useUiStore } from '../stores/uiStore';
import { useWordStore } from '../stores/wordStore';
import { useConfigStore } from '../stores/configStore';
import { api } from '../services/api';
import './ChatDrawer.css';

interface ChatUsage {
  prompt_tokens?: number;
  completion_tokens?: number;
  [key: string]: unknown;
}

interface ChatMessage {
  role: string;
  content: string;
  isError?: boolean;
  usage?: ChatUsage | null;
  model?: string | null;
  costUsd?: number | null;
}

interface ChatDrawerProps {
  mode?: 'bottom' | 'side';
}

const CHAT_MODEL_STORAGE_KEY = 'waw-chat-model';

/**
 * Detect an explicit "add word(s)" command the user TYPED. Strict on purpose:
 * the whole message must be an add/create imperative and every parsed item must
 * be a single word token. Prose or pasted text (multi-word items) returns null,
 * so words that merely appear in context are never treated as a command.
 * Examples that match: "add Jackanapes to Word Content", "create foo, bar and baz".
 */
export function parseAddCommand(text: string): string[] | null {
  const m = text.trim().match(/^(?:add|create)\s+(.+)$/i);
  if (!m) return null;
  let body = m[1]
    // strip a trailing target phrase ("to Word Content", "to the binder", …)
    .replace(/\s+to\s+(?:the\s+)?(?:game\s+)?word\s*content\b.*$/i, '')
    .replace(/\s+to\s+(?:the\s+)?(?:binder|words?)\b.*$/i, '')
    // strip "as a new word" style suffixes
    .replace(/\s+as\s+(?:a\s+)?(?:new\s+)?words?\b.*$/i, '')
    .replace(/[.!]+$/, '')
    .trim();
  if (!body) return null;
  const parts = body.split(/\s*(?:,|;|\band\b|&|\+)\s*/i).map((s) => s.trim()).filter(Boolean);
  const words: string[] = [];
  const seen = new Set<string>();
  for (const p of parts) {
    const w = p.replace(/^["'`]+|["'`]+$/g, '').trim();
    // Single word token only — a multi-word part means this isn't a clean add
    // command (likely prose), so bail to normal chat rather than guess.
    if (!/^[A-Za-z][A-Za-z'’-]*$/.test(w)) return null;
    const key = w.toLowerCase();
    if (!seen.has(key)) { seen.add(key); words.push(w); }
  }
  return words.length ? words : null;
}

export default function ChatDrawer({ mode = 'bottom' }: ChatDrawerProps) {
  const { chatOpen, setChat } = useUiStore();
  const { selectedWord } = useWordStore();
  const { modelRegistry, modelPricing, fetchModelRouting, fetchModelPricing } = useConfigStore();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedModel, setSelectedModel] = useState<string>('');
  // Words awaiting the user's confirmation before we write them. Never write
  // without an explicit yes.
  const [pendingAdd, setPendingAdd] = useState<string[] | null>(null);
  const [addingWords, setAddingWords] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load the registry (same list Settings uses) + pricing for the picker.
  useEffect(() => {
    if (modelRegistry.length === 0) fetchModelRouting();
    fetchModelPricing();
  }, [modelRegistry.length, fetchModelRouting, fetchModelPricing]);

  // Cheapest registry model by (input + output) $/MTok; used as the default so
  // routine chat doesn't burn premium models. Ollama (no price) sorts last.
  const cheapestId = useMemo(() => {
    let best: string | null = null;
    let bestCost = Infinity;
    for (const m of modelRegistry) {
      if (!m.id) continue;
      const p = modelPricing[m.id];
      const cost = p && p.input != null && p.output != null ? p.input + p.output : Infinity;
      if (cost < bestCost) { bestCost = cost; best = m.id; }
    }
    // If nothing is priced yet, prefer a Haiku-ish entry, else the first model.
    if (!best) {
      const haiku = modelRegistry.find((m) => /haiku/i.test(m.displayName) && m.id);
      best = haiku?.id || modelRegistry.find((m) => m.id)?.id || '';
    }
    return best || '';
  }, [modelRegistry, modelPricing]);

  // Initialise the selection: remembered choice if still valid, else cheapest.
  useEffect(() => {
    if (selectedModel || modelRegistry.length === 0) return;
    const remembered = typeof window !== 'undefined' ? localStorage.getItem(CHAT_MODEL_STORAGE_KEY) : null;
    const validRemembered = remembered && modelRegistry.some((m) => m.id === remembered) ? remembered : null;
    setSelectedModel(validRemembered || cheapestId);
  }, [selectedModel, modelRegistry, cheapestId]);

  const onModelChange = useCallback((id: string) => {
    setSelectedModel(id);
    try { localStorage.setItem(CHAT_MODEL_STORAGE_KEY, id); } catch { /* ignore */ }
  }, []);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when drawer opens
  useEffect(() => {
    if (chatOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [chatOpen]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    // First action: "add <word(s)> to Word Content". Only fires on an explicit
    // typed command; shows a confirmation and never writes silently.
    const addWords = parseAddCommand(text);
    if (addWords) {
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: text },
        {
          role: 'assistant',
          content: `I'll create ${addWords.length} Game Word Content record${addWords.length !== 1 ? 's' : ''}: ${addWords.join(', ')}. Confirm below to create ${addWords.length !== 1 ? 'them' : 'it'}.`,
        },
      ]);
      setPendingAdd(addWords);
      setInput('');
      return;
    }

    const userMessage = { role: 'user', content: text };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const result = await api.post('/api/chat', {
        messages: updatedMessages,
        word: selectedWord || null,
        stage: selectedWord?._stage || null,
        model: selectedModel || undefined,
      }) as { message?: ChatMessage; usage?: ChatUsage | null; model?: string | null };

      if (result.message) {
        const usage = result.usage || null;
        const price = selectedModel ? modelPricing[selectedModel] : undefined;
        const costUsd =
          usage && price && price.input != null && price.output != null
            ? ((usage.prompt_tokens || 0) / 1_000_000) * price.input +
              ((usage.completion_tokens || 0) / 1_000_000) * price.output
            : null;
        setMessages((prev) => [
          ...prev,
          { ...result.message!, usage, model: result.model || null, costUsd },
        ]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: (err as Error).message, isError: true },
      ]);
    } finally {
      setLoading(false);
    }
  }, [input, messages, loading, selectedWord, selectedModel, modelPricing]);

  // Save the confirmed words to the Word Bank (POST /api/wordbank/add) and
  // report what happened. Airtable is retired and no longer written here.
  const confirmAdd = useCallback(async () => {
    if (!pendingAdd || addingWords) return;
    setAddingWords(true);
    // Saved to the Word Bank in D1 (Bench, lowercase, unapproved), not
    // Airtable. The server skips any word already in the bank.
    let created: string[] = [];
    let skipped: string[] = [];
    let failed: string[] = [];
    try {
      const res = (await api.post('/api/wordbank/add', { words: pendingAdd })) as {
        created: string[]; skipped: string[]; failed: string[];
      };
      created = res.created || [];
      skipped = res.skipped || [];
      failed = res.failed || [];
    } catch {
      failed = [...pendingAdd];
    }
    const parts: string[] = [];
    if (created.length) parts.push(`Added: ${created.join(', ')}.`);
    if (skipped.length) parts.push(`Already in the binder (skipped): ${skipped.join(', ')}.`);
    if (failed.length) parts.push(`Couldn't add: ${failed.join(', ')}.`);
    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: parts.join(' ') || 'Nothing to add.', isError: failed.length > 0 && created.length === 0 },
    ]);
    setPendingAdd(null);
    setAddingWords(false);
  }, [pendingAdd, addingWords]);

  const cancelAdd = useCallback(() => {
    setPendingAdd(null);
    setMessages((prev) => [...prev, { role: 'assistant', content: 'Okay, nothing created.' }]);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleClear = useCallback(() => {
    setMessages([]);
  }, []);

  if (!chatOpen) return null;

  return (
    <div className={`chat-drawer ${mode === 'side' ? 'chat-side-panel' : 'chat-bottom-panel'}`}>
      <div className="chat-header">
        <span className="chat-title">Chat</span>
        <div className="chat-header-actions">
          {selectedWord && (
            <span className="chat-context-badge">{selectedWord.word}</span>
          )}
          {messages.length > 0 && (
            <button className="chat-clear" onClick={handleClear} title="Clear chat">
              Clear
            </button>
          )}
          <button className="chat-close" onClick={() => setChat(false)} title="Close (Escape)">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <p className="chat-empty">
            Chat with Claude about {selectedWord ? `"${selectedWord.word}"` : 'the current word'}.
            Ask for regenerations, edits, or explanations.
          </p>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={`chat-msg ${msg.role} ${msg.isError ? 'msg-error' : ''}`}>
            <div className="chat-msg-content">{msg.content}</div>
            {msg.role === 'assistant' && !msg.isError && msg.usage && (
              <div className="chat-msg-usage">
                {formatUsage(msg.usage, msg.costUsd, msg.model)}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="chat-msg assistant">
            <div className="chat-msg-content chat-typing">
              <span className="typing-dot" />
              <span className="typing-dot" />
              <span className="typing-dot" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {pendingAdd && (
        <div className="chat-confirm">
          <div className="chat-confirm-text">
            Create {pendingAdd.length} word{pendingAdd.length !== 1 ? 's' : ''} in Game Word Content:{' '}
            <strong>{pendingAdd.join(', ')}</strong>?
          </div>
          <div className="chat-confirm-actions">
            <button className="chat-confirm-cancel" onClick={cancelAdd} disabled={addingWords}>Cancel</button>
            <button className="chat-confirm-create" onClick={confirmAdd} disabled={addingWords}>
              {addingWords ? 'Creating…' : `Create ${pendingAdd.length !== 1 ? 'them' : 'it'}`}
            </button>
          </div>
        </div>
      )}

      <div className="chat-model-bar">
        <label className="chat-model-label" htmlFor="chat-model-select">Model</label>
        <select
          id="chat-model-select"
          className="chat-model-select"
          value={selectedModel}
          onChange={(e) => onModelChange(e.target.value)}
          disabled={loading || modelRegistry.length === 0}
          title="Model for this chat session"
        >
          {modelRegistry.filter((m) => m.id).map((m) => {
            const p = m.id ? modelPricing[m.id] : undefined;
            const priceLabel =
              p && p.input != null && p.output != null ? ` — $${p.input}/$${p.output} per M` : '';
            return (
              <option key={m.id} value={m.id}>
                {m.displayName}{priceLabel}
              </option>
            );
          })}
        </select>
      </div>

      {/* usage helper is defined below the component */}
      <div className="chat-input-area">
        <input
          ref={inputRef}
          type="text"
          placeholder={selectedWord ? `Ask about "${selectedWord.word}"...` : 'Ask about this word...'}
          className="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
        />
        <button
          className="chat-send"
          onClick={handleSend}
          disabled={!input.trim() || loading}
        >
          {loading ? '...' : 'Send'}
        </button>
      </div>
    </div>
  );
}

// Render the per-message usage line: token counts always (they come back with
// every response), plus cost when the selected model has a known price.
function formatUsage(usage: ChatUsage, costUsd: number | null | undefined, model: string | null | undefined): string {
  const inTok = usage.prompt_tokens ?? 0;
  const outTok = usage.completion_tokens ?? 0;
  const parts: string[] = [];
  if (inTok || outTok) parts.push(`${inTok.toLocaleString()} in · ${outTok.toLocaleString()} out tok`);
  if (typeof costUsd === 'number') {
    parts.push(costUsd < 0.01 ? `~$${costUsd.toFixed(5)}` : `$${costUsd.toFixed(4)}`);
  }
  if (model) parts.push(model);
  return parts.join('  ·  ');
}
